export enum DependencyType {
  Undefined = "undefined",
  Exclude = "exclude",
  Include = "include",
  Default = "default",
}

export interface BasicNode {
  id: number;
  created_at?: string;
  updated_at?: string;
}

export interface ApiResponse<Type> {
  data: Type;
  success: boolean;
}

export interface AttributeValue extends BasicNode {
  attribute_id: number;
  attribute_name: string;
  value: string;
}

export interface Asset extends BasicNode {
  preview_image_url: string;
  asset_url: string;
  asset_type?: string;
  attribute_values: AttributeValue[];
  component_view_point_id?: number;
  variation_id?: number;
}

export interface Dependency {
  component: string;
  variation: string;
  group: string;
  type: DependencyType;
}

export interface Variation extends BasicNode {
  key: string;
  original_key: string;
  price: string;
  is_default: boolean;
  is_live?: boolean;
  component_id: number;
  assets: Asset[];
  dependencies?: Dependency[];
}

export interface Component extends BasicNode {
  name: string;
  key?: string;
  asset?: string | null;
  required_choice: boolean;
  multiple: boolean;
  layer_order: number;
  group_id: number;
  order_no: number;
  available_variations: Variation[];
}

export interface ProductAttribute extends BasicNode {
  name: string;
}

export interface ComponentGroup extends BasicNode {
  name: string;
  key?: string;
  product_id?: number;
  components: Component[];
  attributes: ProductAttribute[];
  component_view_points?: ProductViewPoint[];
}

export interface ProductAsset {
  asset: string;
  attribute_values: Record<string, string>;
}

export interface ProductInfo {
  name: string;
  description: string;
}

export interface ProductVersion {
  id?: number;
  product_id?: number;
  name?: string;
  description?: string;
  published_at?: string | null;
  cached_at?: string;
  attribute_values?: {
    value: string;
    attribute_name: string;
  }[];
}

export interface ProductViewPoint {
  name: string;
}

export interface Product extends BasicNode {
  name: string;
  key?: string;
  product_info?: ProductInfo;
  cached_url?: string;
  published_version?: ProductVersion;
  component_groups?: ComponentGroup[];
  assets?: Record<string, ProductAsset[]>;
  price?: string;
  description?: string;
  category?: string | null;
  category_id?: number;
  client_id?: number;
  preview_image?: string;
  product_view_points?: ProductViewPoint[];
  turntable_asset?: string;
  webgl_asset?: string;
  ios_ar_asset?: string;
  ios_ar_assets?: unknown[];
  mobile_ar_asset?: string;
  deleted_at?: string | null;
}

export interface Caption {
  id: number;
  locale_id: number;
  value: string;
  label: string;
}

export interface ApplicationSections {
  id: number;
  name: string;
  keys: {
    id: number;
    key: string;
    captions: Caption[];
  }[];
}

export interface Locale {
  id: number;
  language: string;
  locale: string;
}

export interface ApplicationType extends BasicNode {
  name: string;
}

export interface ApplicationResource extends BasicNode {
  name: string;
  version_id: number;
  api_key: string;
  referrals: string[];
  locales: Locale[];
  types: ApplicationType[];
}

export interface ApplicationSection {
  section_id: number;
  section_name_localized: string;
  section_name: string;
  key_values: Record<string, string>;
}

export interface ApplicationDetails {
  id: number;
  name: string;
  type: string;
  locale: string;
  sections: ApplicationSection[];
}

export interface ApplicationSectionV2 {
  key: string;
  name: string;
  fields: Record<string, string>;
}

export interface ApplicationDetailsV2 {
  name: string;
  sections: ApplicationSectionV2[];
}

export interface VTGComponent extends Component {
  idxInGroup: number;
  groupName: string;
  groupId: number;
  groupIndex: number;
}

export interface VTGComponentGroup extends ComponentGroup {
  components: VTGComponent[];
}

export type ActiveComponentGroup = {
  name: string;
  id: number;
  index: number;
};

export type ActiveComponent = {
  name: string;
  componentId: number;
  index: number;
};

export interface CurrentLocaleModel {
  locale: string;
  localeID: string;
}
